import { Controller } from "@hotwired/stimulus"
import { escapeHtml, safeUrl } from "../helpers/html"

export default class extends Controller {
  static targets = ["title", "options", "backButton", "backPlaceholder", "card", "header", "scroller"]
  static values = { data: Object }

  connect() {
    this.nodes = {}
    this.history = []
    
    // 1. Skapa/Hämta Session ID
    this.sessionId = sessionStorage.getItem('kliniko_session')
    if (!this.sessionId) {
      this.sessionId = Math.random().toString(36).substring(2, 15)
      sessionStorage.setItem('kliniko_session', this.sessionId)
    }

    // Widgeten laddas medan modalen är dold (vh = 0, bredd = 0), så höjden
    // måste mätas om när den visas, och när innehållet byter storlek
    if (this.hasCardTarget) {
      this.resizeObserver = new ResizeObserver(() => this.sendHeightToParent())
      this.resizeObserver.observe(this.cardTarget)
      this.resizeObserver.observe(this.titleTarget)
      this.resizeObserver.observe(this.optionsTarget)
    }

    this.startFlow()
    this.trackEvent('view', 'start', 'Widget Loaded')
  }

  disconnect() {
    this.resizeObserver?.disconnect()
  }

  startFlow() {
    let rawData = this.dataValue
    if (rawData.graph) rawData = rawData.graph
    if (rawData.drawflow) rawData = rawData.drawflow
    
    const modules = Object.keys(rawData)
    if (modules.length === 0) return this.showError("Ingen data.")
    
    this.nodes = rawData[modules[0]].data
    
    const startNode = Object.values(this.nodes).find(n => n.name === 'start')
    if (!startNode) return this.showError("Start saknas.")

    const outputs = startNode.outputs
    if (outputs && outputs.output_1 && outputs.output_1.connections.length > 0) {
      this.renderNode(outputs.output_1.connections[0].node)
    }
  }

  renderNode(nodeId) {
    const node = this.nodes[nodeId]
    if (!node) return

    this.currentNodeId = nodeId
    this.updateBackButton()

    if (node.name === 'question') {
      this.renderQuestion(node)
    } else if (node.name === 'booking') {
      this.renderBooking(node)
    } else if (node.name === 'comparison') {
      this.renderComparison(node)
    } else if (node.name === 'text') {
      this.renderText(node)
    }
    this.sendHeightToParent()
  }

  renderQuestion(node) {
  this.titleTarget.innerText = node.data.question || "Välj alternativ"
  
  this.optionsTarget.innerHTML = ''
  this.optionsTarget.className = ''
  const answers = node.data.answers || []

  // Check actual container width instead of viewport breakpoint
  const isWide = this.element.offsetWidth > 420

  if (answers.length > 1 && isWide) {
    this.optionsTarget.classList.add("grid", "grid-cols-2", "gap-4")
  } else {
    this.optionsTarget.classList.add("flex", "flex-col", "gap-3")
  }

  answers.forEach((ans, index) => {
    const btn = document.createElement('button')
    
    btn.className = `
      p-4 border-2 rounded-2xl text-left transition-all relative flex flex-col h-full w-full
      border-gray-100 hover-border-primary hover-bg-accent
      animate-fade-in-up items-center justify-center
    `
    
    btn.dataset.action = "click->widget#handleAnswer"
    btn.dataset.nodeId = this.currentNodeId
    btn.dataset.outputIndex = index + 1

    btn.innerHTML = `<span class="font-bold text-base sm:text-lg text-slate-800">${escapeHtml(ans)}</span>`
    btn.style.animationDelay = `${index * 0.1}s`
    this.optionsTarget.appendChild(btn)
  })
}

  renderBooking(node) {
    this.titleTarget.innerText = "Boka tid"
    this.optionsTarget.className = "flex flex-col gap-4"
    this.optionsTarget.innerHTML = ''

    const url = safeUrl(node.data.url)
    this.trackEvent('conversion_view', this.currentNodeId, 'Booking Page Shown')

    const card = document.createElement('div')
    card.className = `
      p-6 sm:p-8 border-2 rounded-2xl text-center relative flex flex-col items-center
      border-primary bg-accent shadow-lg animate-fade-in-up
    `
    
    card.innerHTML = `
      <span class="absolute -top-3 bg-primary text-white text-[10px] uppercase font-bold px-3 py-1 rounded-full shadow-lg">
        Rekommenderas
      </span>
      
      <div class="text-3xl sm:text-4xl mb-3 sm:mb-4">📅</div>
      <h3 class="font-bold text-lg mb-1">Boka tid online</h3>
      <p class="text-sm text-primary mb-6 leading-snug">Vi har hittat en ledig tid för dig.</p>

      <a href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer" 
         class="inline-block w-full px-6 py-3 bg-primary text-white rounded-xl font-bold text-center hover:opacity-90 transition-opacity">
        Öppna Bokning
      </a>
    `
    
    this.optionsTarget.appendChild(card)
    
    const link = card.querySelector('a')
    link.addEventListener('click', () => {
      this.trackEvent('conversion_click', this.currentNodeId, node.data.url)
    })
  }

  renderText(node) {
    this.titleTarget.innerText = node.data.title || ""
    this.optionsTarget.className = "animate-fade-in-up"

    const paragraph = document.createElement('p')
    paragraph.className = "text-slate-600 leading-relaxed whitespace-pre-line"
    paragraph.textContent = node.data.text || ""
    this.optionsTarget.replaceChildren(paragraph)

    this.trackEvent('text_view', this.currentNodeId, node.data.title)
  }

  renderComparison(node) {
    this.titleTarget.innerText = node.data.question || "Välj bokningstyp"
    this.optionsTarget.className = "grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-4"
    this.optionsTarget.innerHTML = ''

    const isDemoMode = node.data.demo_mode === true || node.data.demo_mode === 'true'

    // --- NY LOGIK HÄR ---
    // Hämtar data dynamiskt men faller tillbaka på gamla 'vgr_id'/'private_id' om det behövs
    const left = {
      id: node.data.left_id || node.data.vgr_id,
      title: node.data.left_title || "Regionen",
      price: node.data.left_price || "300 kr (frikort gäller)",
      time: isDemoMode ? this.buildDemoTime(10, 9, 40) : node.data.left_time,
      highlight: node.data.highlight === 'left',
      booking_type: node.data.left_booking_type || ''
    }

    const right = {
      id: node.data.right_id || node.data.private_id,
      title: node.data.right_title || "Privat bokning",
      price: node.data.right_price || "1200 kr",
      time: isDemoMode ? this.buildDemoTime(2, 14, 20) : node.data.right_time,
      // Default highlight på höger om inget anges (för bakåtkompatibilitet med privat)
      booking_type: node.data.right_booking_type || '',
      highlight: node.data.highlight === 'right' || (!node.data.highlight && node.data.private_id)
    }

    this.trackEvent('comparison_view', this.currentNodeId, 'Comparison Page Shown')

    const createCard = (item, outputIndex) => {
      const btn = document.createElement('button')
      
      // Bas-klasser
      let classes = `
        text-left p-5 sm:p-6 border-2 rounded-2xl transition-all relative flex flex-col h-full w-full
        bg-white border-gray-100 hover-border-primary hover-bg-accent animate-fade-in-up group
      `
      
      // Highlight styling
      if (item.highlight) {
        // Ta bort bg-white och lägg till accent
        classes = classes.replace('bg-white', 'bg-accent shadow-lg border-primary')
      }

      btn.className = classes

      let content = ''
      
      if (item.highlight) {
        content += `<span class="absolute -top-3 left-6 bg-primary text-white text-[10px] uppercase font-bold px-3 py-1 rounded-full shadow-lg">Rekommenderas</span>`
      }

      content += `
        <div class="mb-4">
          <h3 class="font-bold text-lg text-slate-800 group-hover:text-primary">${escapeHtml(item.title)}</h3>
          <p class="text-xs text-slate-500 font-medium">${escapeHtml(item.price)}</p>
        </div>
        
        <div class="mt-auto pt-4 w-full border-t border-gray-200 group-hover:border-primary/30">
          <p class="text-[10px] uppercase font-bold text-slate-400 tracking-wider mb-1">Nästa lediga tid</p>
          <div class="time-slot font-bold text-lg text-primary flex items-center gap-2">
            ${item.time
              ? `<span>${escapeHtml(item.time)}</span>`
              : `<span class="inline-block w-4 h-4 border-2 border-slate-200 border-t-primary rounded-full animate-spin"></span><span class="text-sm text-slate-400 font-normal">Söker tid...</span>`}
          </div>
        </div>
      `
      btn.innerHTML = content
      
      // Skicka med titel så vi kan logga vad de klickade på
      btn.onclick = () => this.handleComparisonChoice(node, outputIndex, item.title)
      
      return { btn, timeSlot: btn.querySelector('.time-slot') }
    }

    const leftCard = createCard(left, 1)
    const rightCard = createCard(right, 2)

    // Rendera ordning (Privat/Highlight brukar man vilja ha först/tydligast på mobil)
    // Här lägger vi dem i "naturlig" ordning (Left = 1, Right = 2). 
    // Om du vill byta plats på mobil kan du använda CSS 'order-last' på den ena.
    // I din gamla kod lades 'privateCard' till först i DOM, vi gör samma sak om Right är highlightad.
    if (right.highlight) {
        this.optionsTarget.appendChild(rightCard.btn)
        this.optionsTarget.appendChild(leftCard.btn)
    } else {
        this.optionsTarget.appendChild(leftCard.btn)
        this.optionsTarget.appendChild(rightCard.btn)
    }

    // 2. Hämta live-tider endast utanför demo-läget.
    if (!isDemoMode && (left.id || right.id)) {
      // Vi mappar nya ID:n till de parametrar backend förväntar sig (vgr_id/private_id)
      // så slipper du deploya om backend just nu.
      const query = new URLSearchParams()
      if (left.id) query.set('vgr_id', left.id)
      if (right.id) query.set('private_id', right.id)
      if (left.booking_type) query.set('left_booking_type', left.booking_type)
      if (right.booking_type) query.set('right_booking_type', right.booking_type)

      fetch(`/api/availability?${query.toString()}`)
        .then(res => res.json())
        .then(data => {
          // Backend returnerar json { vgr: "Tid", private: "Tid" }
          // Eftersom vi mappade left -> vgr och right -> private ovan så stämmer det här:
          if (leftCard.timeSlot) leftCard.timeSlot.textContent = data.vgr
          if (rightCard.timeSlot) rightCard.timeSlot.textContent = data.private
        })
        .catch(err => {
          console.error(err)
          if (leftCard.timeSlot) leftCard.timeSlot.textContent = "Se kalender"
          if (rightCard.timeSlot) rightCard.timeSlot.textContent = "Snabbast tid"
        })
    }
  }

  handleComparisonChoice(node, outputIndex, label) {
    const outputKey = `output_${outputIndex}`
    this.history.push(node.id)
    
    // Använd dynamisk label om den finns, annars fallback
    const trackingLabel = label || (outputIndex === 1 ? 'Alternativ 1' : 'Alternativ 2')
    this.trackEvent('comparison_click', this.currentNodeId, trackingLabel)

    if (node.outputs[outputKey] && node.outputs[outputKey].connections.length > 0) {
      const nextNodeId = node.outputs[outputKey].connections[0].node
      this.renderNode(nextNodeId)
    } else {
      console.log("Ingen koppling för val " + outputIndex)
    }
  }

  handleAnswer(event) {
    const btn = event.currentTarget
    
    btn.classList.add('btn-selected')
    btn.classList.remove('border-gray-100', 'hover-border-primary', 'hover-bg-accent')
    
    const siblings = this.optionsTarget.querySelectorAll('button')
    siblings.forEach(s => {
      if(s !== btn) s.classList.add('opacity-50')
    })

    const nodeId = btn.dataset.nodeId
    const outputIndex = btn.dataset.outputIndex
    const outputKey = `output_${outputIndex}`
    const label = btn.querySelector('span').innerText
    const node = this.nodes[nodeId]
    const questionText = node.data.question || "Okänd fråga"
    
    this.history.push(nodeId)
    this.trackEvent('click', nodeId, label, { question: questionText })

    if (node.outputs[outputKey] && node.outputs[outputKey].connections.length > 0) {
      setTimeout(() => {
        this.renderNode(node.outputs[outputKey].connections[0].node)
      }, 200) 
    }
  }

  goBack() {
    if (this.history.length === 0) return
    const prevNodeId = this.history.pop()
    this.renderNode(prevNodeId)
  }

  updateBackButton() {
    if (this.history.length > 0) {
      this.backButtonTarget.classList.remove('hidden')
      this.backPlaceholderTarget.classList.add('hidden')
    } else {
      this.backButtonTarget.classList.add('hidden')
      this.backPlaceholderTarget.classList.remove('hidden')
    }
  }

  sendHeightToParent() {
    if (window.parent === window || !this.hasCardTarget) return

    cancelAnimationFrame(this.resizeFrame)
    this.resizeFrame = requestAnimationFrame(() => {
      // Kortets naturliga höjd, oberoende av min-h/max-h i vh (som är 0 när modalen är dold)
      const card = getComputedStyle(this.cardTarget)
      const header = getComputedStyle(this.headerTarget)
      const height = Math.ceil(
        parseFloat(card.paddingTop) + parseFloat(card.paddingBottom) +
        this.headerTarget.offsetHeight + parseFloat(header.marginBottom) +
        this.scrollerTarget.scrollHeight
      )
      if (height === this.lastSentHeight) return

      this.lastSentHeight = height
      window.parent.postMessage({ type: 'kliniko-resize', flowId: this.dataValue.id, height: height }, '*')
    })
  }

  showError(msg) {
    this.titleTarget.innerText = "Fel"
    this.optionsTarget.textContent = msg
  }

  trackEvent(type, nodeId, label, meta = {}) {
    // The editor preview has no flow id; don't log its clicks as real traffic
    if (!this.dataValue.id) return

    const payload = {
      flow_id: this.dataValue.id,
      event_type: type,
      node_id: nodeId,
      label: label,
      session_id: this.sessionId,
      meta: meta
    }

    const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' })
    navigator.sendBeacon('/api/track', blob)
  }

  buildDemoTime(daysAhead, hour, minute) {
    const date = new Date()
    date.setDate(date.getDate() + daysAhead)
    date.setHours(hour, minute, 0, 0)

    const months = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec']
    const hh = String(date.getHours()).padStart(2, '0')
    const mm = String(date.getMinutes()).padStart(2, '0')

    return `${date.getDate()} ${months[date.getMonth()]} ${hh}:${mm}`
  }
}